/** The town painting: the street plan, the shadows, the air, and one
 * `<TownBuilding>` per built slot, with the rules the picture owns.
 *
 * Split out of `TownScreen.tsx` on 2026-09-17 unchanged; that file keeps the
 * component and the foot bar and re-exports every symbol it exported before.
 * Nothing here reads a rule (I10): every number is a look-up in a table this
 * file declares or a value the slot grid already resolved.
 */

import { useMemo } from "react";
import type { BuildingDef } from "@game/core";
import { buildingIcon } from "../art.js";
import { ScaledSprite, useSpriteSize } from "../primitives.js";
import type { AirRows, ResolvedSlot, SkyLift, TownSlot } from "../townSlots.js";
import {
  AIR_CLEAR_Y, AIR_FULL_Y, AIR_HALF_AMOUNT, AIR_HALF_Y, AIR_SPLIT_Y, BAND_HAZE, TOWN_CANVAS_H,
  TOWN_CANVAS_W, TOWN_HD, bandScale, expectedHeight,
} from "../townSlots.js";
import {
  COBBLE_SIZE, COBBLE_TILES, cobbleTile, polygonPoints, townPathPlan,
} from "../townPaths.js";
import type { Prop } from "../townScenery.js";
import { LIFE_CSS } from "../townLife.js";
import { SCENE_CSS } from "../townScene.js";
import type { TownProp } from "../townProps.js";
import { PROPS_CSS } from "../townPropsView.js";
import { StyleLayer, useStyleSheet } from "../../theme/styleSheet.js";

// ------------------------------------------------------- the painting's own CSS

const PAINT_STYLE_ID = "hs-town-paint-styles";

/**
 * The rules this screen's painting owns, laid over the shared town sheet.
 *
 * They are here rather than in `styles.ts` or `townChrome.tsx` because those
 * two files belong to the chrome and the theme, and every rule below is a
 * decision about the PICTURE: what a shadow is, what an unbuilt lot looks
 * like, what a fire does. Each one is written at one class more than the rule
 * it replaces, and each says which rule that is.
 */
export const PAINT_CSS = `
/* --- one shadow model ---------------------------------------------------
   styles.ts throws the sprite's own silhouette 8px down and right at zero
   blur, and TownStreets separately draws a cast polygon and two contact
   pools on the ground. Two shadow systems in one frame, and the offset
   silhouette is the one that cannot be right: a silhouette translated puts a
   dark slab along every SKY-facing edge as well - roof ridges, spires, the
   top of a standing cactus - where nothing can be casting onto anything. It
   is kept, at 2px, for the one job it does do: twelve Castle structures share
   one cobalt slate at one value, and without a dark lip between them the left
   third of the frame reads as a single blue mass. The ground shadow is the
   SVG layer's. */
.hs-town .hs-town__art {
  filter: var(--hs-roof, brightness(1))
    drop-shadow(2px 1px 0 rgba(20,14,6,0.5));
}
/* --- the cast, shaped like its caster ------------------------------------
   The atlas cell again, flattened about the sprite's own foot so it lies on
   the ground in front of it, sheared right along the same sun vector as
   SUN_CAST, painted flat black (brightness(0) keeps the alpha and throws
   away the colour) and blurred by about a roof tile. Behind the sprite in
   the same box, so it can never cover its own caster, and low enough in
   value that it reads as the ground going dark rather than as a second
   building lying down. */
.hs-town .hs-town__silcast {
  position: absolute;
  inset: 0;
  transform-origin: 50% 100%;
  transform: scaleY(-0.30) skewX(34deg);
  filter: brightness(0) blur(5px);
  opacity: 0.34;
  pointer-events: none;
  z-index: 0;
}
.hs-town .hs-town__slot .hs-town__art { position: relative; z-index: 1; }
.hs-town .hs-town__canvas .hs-town__prop .hs-town__art {
  filter: var(--hs-prop-tint, brightness(1)) var(--hs-grade, brightness(1))
    drop-shadow(2px 1px 0 rgba(20,14,6,0.45));
}
/* --- nothing in the near plane is pure black -----------------------------
   Two findings, one cause. "Saltreach's palms have a pure-black matte hole punched
   through the centre of the frame - 22.4% of that 400x400 region is pure
   black, hard-edged, following the frond silhouette, not shading", and "a
   hard 2px near-black keyline rings every one of them, visible against sky".
   The hole is a keying failure in the sprite: measured over its own opaque
   pixels obs_palm_cluster is 9.8% #000 and obs_mangrove_root 29.9%, where
   the rest of that country's planting runs 0.5 to 2.7%. Those two are out of
   the tables (see townScenery) - but every large sprite in this set carries
   some black in its own outline, and magnified to a foreground occluder an
   outline drawn at 1x is a keyline drawn at 2.5.
 
   invert-brightness-invert is a BLACK LIFT and it is the one curve a CSS
   filter chain can express that does what is wanted here: it maps v to
   1-k+kv, so white stays white and 0 comes up to 1-k, and it costs only the
   contrast it lifts. At 0.86 the deepest pixel an occluder can contain is 36
   rather than 0, which is dark enough to still read as the shade side and
   light enough that a viewer cannot find a hole in it. It runs FIRST, so the country's own tint and the frame's
   grade are applied to a sprite that no longer has a floor in it.
 
   And the silhouette drop-shadow is gone from this chain entirely. It was
   3px down and right at zero blur, which on a sprite this size is exactly the
   keyline the critic measured - and it cannot be right for a foreground
   occluder anyway: the thing is at the viewer's own distance and there is
   nothing behind it near enough to receive a shadow. What is left is the rim,
   which is light rather than dark and is thrown toward the sun. */
.hs-town .hs-town__canvas .hs-town__prop--fg .hs-town__art {
  filter: invert(1) brightness(0.86) invert(1)
    var(--hs-fg-tint, brightness(1)) var(--hs-prop-tint, brightness(1))
    var(--hs-grade, brightness(1))
    var(--hs-fg-rim, drop-shadow(0 0 0 rgba(0,0,0,0)));
}

/* --- a fire is not lit by its country -----------------------------------
   --hs-prop-tint rotates a prop's hue into the country it stands in - 150
   degrees for the graveyard, 214 for the cavern, 168 for the snowfield - and
   a campfire rotated 150 degrees is CYAN. A flame throws light, it does not
   take it, so these leave the tint chain and keep their own colour in all
   thirteen towns. The warm drop-shadow is the light they throw. */
.hs-town .hs-town__canvas .hs-town__prop--lit .hs-town__art {
  filter: brightness(1.04) saturate(1.12)
    drop-shadow(0 0 7px rgba(255,164,64,0.5))
    drop-shadow(0 0 16px rgba(255,120,32,0.28))
    drop-shadow(2px 1px 0 rgba(20,14,6,0.45));
}

/* --- the unbuilt lot ----------------------------------------------------
   A CSS gold plus-sign with a blurred drop-shadow, drawn at every slot the
   town has not built yet: on Prismgate it floats over the plaza at (240,340)
   and on Ironhorde over the backdrop at (140,340), unattached to anything,
   with no post, no base and no ground contact - the only object in the frame
   that is not pixel art. It is a hover affordance, so it waits for the
   cursor. */
.hs-town .hs-town__ghost::after { opacity: 0; transition: opacity 90ms linear; }
.hs-town .hs-town__ghost:hover::after,
.hs-town .hs-town__ghost:focus-visible::after { opacity: 1; }
.hs-town .hs-town__ghost--blocked:hover::after { opacity: 0.3; }

/* --- the graveyard's own occluder ---------------------------------------
   townChrome gives the cavern and the snowfield an --hs-fg-rim and leaves the
   graveyard on the default, which is a transparent drop-shadow: no rim at
   all. That is why the menhir reads as a flat grey bar - it is light grey
   granite with no chroma for saturate() to scale and a smooth face with
   nothing on it to catch a light. A rim is the one thing a filter CAN add
   that the sprite does not already contain: a light drop-shadow thrown up and
   left, which is where this screen's sun is, paints a lit lip along the
   silhouette it clears. Pale lichen green rather than white, because the
   graveyard's ground tint rotates 150 degrees and a neutral rim comes back
   out of it neutral - the one colour a stone in a green crypt should not be.
   No --hs-fg-tint: the tint chain is what took Ironhorde's stones to a pale
   blue-white, and a brightness on grey granite is still grey granite. */
.hs-town .hs-town__canvas[data-ground="grave"] {
  --hs-fg-rim: drop-shadow(-1px -1px 0 rgba(196,214,168,0.38));
}

/* --- the coast ----------------------------------------------------------
   Saltreach's ninth country. The eight in styles.ts are keyed by data-ground
   and this is the ninth key, so it needs its own light or it falls through to
   an untinted default and its paving comes out the lawn's cool limestone. */
.hs-town__canvas[data-ground="coast"] {
  --hs-prop-tint: brightness(1.03) saturate(1.18) hue-rotate(-8deg);
  --hs-ground-tint: brightness(1.04) saturate(1.35) hue-rotate(-12deg);
  --hs-haze-1: rgba(226,214,178,0.28); --hs-haze-2: rgba(208,196,158,0.18);
  --hs-haze-0: rgba(226,214,178,0); --hs-haze-3: rgba(208,196,158,0);
}

/* --- Saltreach's buildings have no floor either -------------------------------
   The occluder rule above lifts the black out of the near plane; this is the
   same lift on the same country's BUILDINGS, and it is here because the last
   visible instance of the defect turned out to be inside the building art
   rather than in the planting. Measured on the shipped frame: a 40x90 device
   slab of pure #000 fills the shade side of the maelstrom's monolith at
   device x 1595-1640, y 690-765 - contiguous forty-pixel runs of (0,0,0) on
   the sampled rows - and it stands directly against the brightest white foam
   in the picture, so it reads as a hole punched through the canvas rather
   than as a face turned away from the sun. Saltreach measured 2.31% pure black
   over the whole painting against Castle's 0.27%.

   Re-keying the sprite is the right repair and it is in the atlas, not here.
   What CAN be done from the screen is put a floor under the value range:
   invert-brightness-invert maps v to 1-k+kv, so at 0.90 the deepest pixel any
   Saltreach building can contain is 25 and the white foam is untouched. It costs a
   tenth of the contrast on one town's buildings, and it is scoped to
   .hs-town__slot so the props keep their own chain. */
.hs-town .hs-town__canvas[data-ground="coast"] .hs-town__slot .hs-town__art {
  filter: invert(1) brightness(0.90) invert(1)
    var(--hs-roof, brightness(1))
    drop-shadow(2px 1px 0 rgba(20,14,6,0.5));
}

/* --- the shaft of sun through the canopy ---------------------------------
   townChrome draws Greenmantle one highlight, because that screen has none of its
   own: p99 luma 197 against Seiliedh's 250, and no global curve invents a
   highlight from a histogram that stops there. What it drew was a 107-degree
   linear-gradient at 0.94 alpha with smooth stops and a wide radial bloom
   behind it, and a critic reported it twice - "the single clearest 'this is
   CSS, not pixel art' tell in the twelve... a continuous anti-aliased ramp
   that milks out the conifers, the mountains and the dome it passes over, and
   it kills contrast in the exact area the composition wants to read first".

   Two changes, and the second is the one that matters. A CSS gradient
   interpolates unless two stops share a position, so every stop here has a
   twin and the falloff is a flat three-step ramp - which is what a pixel
   painter dithers rather than blends. And the shaft is NARROW: two bands
   totalling under a fiftieth of the gradient line where the old one ran a
   tenth of it. A god-ray is a gap in a canopy, not a pane of glass laid over
   the upper third of the picture - drawn wide, a stepped ramp reads as
   frosted glass, which is what a first stepped draft of this measured as. The
   bloom is gone with it: that layer is what crossed the road.

   The variables are overridden here rather than edited in townChrome because
   the defect is in the PICTURE and this file is the picture's. townChrome's
   rule is one class plus one attribute; this is two classes plus one, so it
   wins on specificity as well as on order. */
.hs-town .hs-town__canvas[data-faction="greenmantle"] {
  --hs-glow-size: 100% 60%;
  --hs-glow-mask: linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 30%,
    rgba(0,0,0,0.62) 30%, rgba(0,0,0,0.62) 42%,
    rgba(0,0,0,0.30) 42%, rgba(0,0,0,0.30) 52%,
    rgba(0,0,0,0.12) 52%, rgba(0,0,0,0.12) 60%, rgba(0,0,0,0) 60%);
  --hs-glow:
    linear-gradient(107deg, rgba(0,0,0,0) 29.2%, rgba(252,246,214,0.52) 29.2%,
      rgba(252,246,214,0.52) 30.0%, rgba(240,232,194,0.26) 30.0%,
      rgba(240,232,194,0.26) 30.7%, rgba(0,0,0,0) 30.7%),
    linear-gradient(107deg, rgba(0,0,0,0) 34.4%, rgba(246,238,202,0.30) 34.4%,
      rgba(246,238,202,0.30) 34.9%, rgba(230,222,186,0.15) 34.9%,
      rgba(230,222,186,0.15) 35.4%, rgba(0,0,0,0) 35.4%);
}

/* --- the verge's outer ring ---------------------------------------------
   The third and faintest of the three: see TownPathPlan.halos. Twenty-four
   pixels wide now rather than thirteen and correspondingly fainter, because
   what it has to do is stop being a ring and start being the ground getting
   trodden - a critic read the streets as ribbons "terminating at the frame
   edge" with "no verge". */
.hs-town .hs-town__halo { fill: #5b4a2c; fill-opacity: 0.085; }

/* --- where a prop meets the floor ---------------------------------------
   The two ellipses under every prop are radial gradients: they fall to zero
   at their own rim, so on a 30-pixel prop there is no row in either of them
   that is unambiguously dark, and a critic measured the result exactly -
   "front-band props have no contact shadow at all - they are stickers on the
   plaza... the Emberpit boulder is a perfect sphere resting on flat paving
   with zero darkening underneath". This is the flat one: a hard-edged fill,
   offset down and right on the same key the buildings cast on, drawn over
   both gradients. crispEdges on the SVG keeps its rim a two-value step
   rather than an anti-aliased one, which is the difference between a contact
   shadow and an airbrush smudge in a frame drawn like this. */
.hs-town .hs-town__seat { fill: #17110a; fill-opacity: 0.62; }

/* --- the grade on the terraced backdrops ---------------------------------
   townChrome's per-faction grades were fitted to the flat backdrops of
   2026-09-01, by inverting the first round's numbers out of a shot and
   re-solving against the reference's histogram. The terraced backdrops are
   new paintings, quantised to their own 64-colour tone, and the three dark
   towns took their old grades on top of it: Emberpit at brightness 0.74 and
   Fenmire at 0.68 lost their buildings into the ground entirely on the first
   terraced shot. Neutral here, at one class more than townChrome's rule, for
   the three that went under; the rest still read. */
.hs-town .hs-town__canvas[data-faction="emberpit"]    { --hs-grade: brightness(1.00) contrast(1.02); }
.hs-town .hs-town__canvas[data-faction="fenmire"]   { --hs-grade: brightness(1.00) contrast(1.04); }
.hs-town .hs-town__canvas[data-faction="gravewatch"] { --hs-grade: brightness(0.96) contrast(1.00); }
/* The thirteenth town. townChrome's per-faction grade table predates
   Blackwater; its buildings take the same near-neutral grade as the other
   dusk towns, and its backdrop, like every backdrop now, takes none. */
.hs-town .hs-town__canvas[data-faction="blackwater"] { --hs-grade: brightness(1.00) contrast(1.02); }

/* --- the one thing that moves -------------------------------------------
   There was no @keyframes and no requestAnimationFrame anywhere in the
   town module, so every fire in every town was frozen. A pixel fire does not
   ease: the flicker steps between four states and holds each one, which is
   what a hand-animated four-frame flame does. Behind a motion query, so the
   screenshot harness - which asks for reduced motion - still photographs one
   deterministic frame. */
@media (prefers-reduced-motion: no-preference) {
  .hs-town .hs-town__prop--lit .hs-town__art {
    animation: hs-town-flicker 1.15s steps(1, end) infinite;
  }
  .hs-town .hs-town__glow { animation: hs-town-breathe 7s ease-in-out infinite; }
}
@keyframes hs-town-flicker {
  0%   { filter: brightness(1.04) saturate(1.12)
           drop-shadow(0 0 7px rgba(255,164,64,0.5))
           drop-shadow(0 0 16px rgba(255,120,32,0.28))
           drop-shadow(2px 1px 0 rgba(20,14,6,0.45)); }
  28%  { filter: brightness(1.12) saturate(1.18)
           drop-shadow(0 0 9px rgba(255,178,80,0.62))
           drop-shadow(0 0 20px rgba(255,120,32,0.34))
           drop-shadow(2px 1px 0 rgba(20,14,6,0.45)); }
  55%  { filter: brightness(0.98) saturate(1.08)
           drop-shadow(0 0 6px rgba(255,150,56,0.42))
           drop-shadow(0 0 13px rgba(255,120,32,0.22))
           drop-shadow(2px 1px 0 rgba(20,14,6,0.45)); }
  78%  { filter: brightness(1.08) saturate(1.16)
           drop-shadow(0 0 8px rgba(255,172,72,0.56))
           drop-shadow(0 0 18px rgba(255,120,32,0.30))
           drop-shadow(2px 1px 0 rgba(20,14,6,0.45)); }
}
@keyframes hs-town-breathe {
  0%, 100% { opacity: 0.92; }
  50%      { opacity: 1; }
}
`;

/** The `overlay` layer, above `TownStyles`, so an equal-specificity rule of ours wins. */
export function TownPaintStyles(): null {
  useStyleSheet(PAINT_STYLE_ID, PAINT_SHEET, StyleLayer.overlay);
  return null;
}

const PAINT_SHEET = PAINT_CSS + SCENE_CSS + LIFE_CSS + PROPS_CSS;

// ------------------------------------------------------------ the ground

/** The cobble pattern is one `<defs>` for the screen, so the ids are fixed. */
const COBBLE_ID = "hs-town-cobble";
const MOTTLE_ID = "hs-town-mottle";
const DEPTH_ID = "hs-town-depth";
const SHADOW_ID = "hs-town-shadow";

const BANDS = ["back", "middle", "front"] as const;

/**
 * Where a cast shadow lands, as a fraction of the caster's own drawn width.
 *
 * The sun is upper left - it is upper left in `.hs-town__light`'s gradient, in
 * every sprite's own painted lighting and in the `drop-shadow` on
 * `.hs-town__art` - so the shadow goes down and to the RIGHT. Down, on this
 * screen, is toward the viewer: the ground plane recedes upward, so a shadow
 * thrown forward is what lands on the street the player is looking across
 * rather than disappearing behind the wall that threw it.
 *
 * 0.62 of a width is a long shadow, and that is the point. At 0.2 it stays
 * inside the building's own apron and is indistinguishable from the contact
 * pool; at 0.62 a front-band building's shadow crosses the promenade and a
 * middle-band one reaches the roof line of whatever stands in front of it.
 */
const SUN_CAST = Object.freeze({ x: 0.62, y: 0.155 });

/**
 * How dark a shadow is at a given canvas row.
 *
 * The back band's treeline is planted on the PAINTED hills - its ground lines
 * run to canvas row 266, which is above the seam where the painting stops and
 * the flat ground plane starts - so its contact pools land on a hillside a
 * quarter of a mile away. At the near plane's strength they read as holes
 * punched in the backdrop, and a critic finds them as "dark blobs floating in
 * the sky". Aerial perspective applies to a shadow like anything else: it is
 * the same wash over the same distance, so a shadow at the far band's floor
 * keeps a third of its weight and one at the front band's floor keeps all of
 * it. Between the two it is linear, which is what the airlight does as well.
 */
const SHADOW_FAR = 0.32;

const shadowStrength = (y: number): number => {
  const t = (y - AIR_FULL_Y) / (AIR_CLEAR_Y - AIR_FULL_Y);
  return SHADOW_FAR + (1 - SHADOW_FAR) * Math.max(0, Math.min(1, t));
};

/** One shape in the shadow layer: a thrown cast, a contact pool, or a seat. */
type Shadow =
  | { readonly kind: "cast"; readonly points: string; readonly a: number }
  | {
    readonly kind: "pool"; readonly x: number; readonly y: number;
    readonly rx: number; readonly ry: number; readonly k: 0 | 1; readonly a: number;
  }
  | {
    readonly kind: "seat"; readonly x: number; readonly y: number;
    readonly rx: number; readonly ry: number; readonly a: number;
  }
  /**
   * The shadow a WALL casts where it meets the ground: a flat band the
   * width of the building's own footprint, not an ellipse.
   *
   * The blind critic on the Castle keep: "its wall base meets the pale
   * plateau on a soft airbrushed dark ellipse rather than a cast shadow
   * shaped like the walls... it reads as a model set on a table." A
   * building's footprint is a rectangle; an ellipse under it is dark in the
   * middle and gone at the corners, which is the one place a wall actually
   * touches the ground.
   */
  | {
    readonly kind: "base"; readonly x: number; readonly y: number;
    readonly w: number; readonly h: number; readonly a: number;
  };

/** Painter's order runs over buildings and props together. */
export type StageItem =
  | { readonly kind: "slot"; readonly y: number; readonly x: number; readonly r: ResolvedSlot }
  | { readonly kind: "prop"; readonly y: number; readonly x: number; readonly prop: Prop; readonly i: number }
  | { readonly kind: "hdprop"; readonly y: number; readonly x: number; readonly hd: TownProp; readonly i: number };

/**
 * The street plan, drawn under every building.
 *
 * Under, not interleaved: a street is always farther than the band it serves,
 * so a building lapping over the road in front of it is exactly right and one
 * layer gets the depth for free. The geometry comes from `townPaths.ts`, which
 * derives it from the same slot table the buildings stand on.
 *
 * Rasterised at *device* resolution rather than in canvas units: the stage sits
 * under one `transform: scale()`, so an SVG authored at 1280x720 is drawn at
 * 1280x720 and then resampled, which is what put a soft three-tone blend on
 * every kerb in an otherwise hard-edged pixel scene. Sized at the scaled
 * dimensions and scaled back down, the vector rasteriser lands on the device
 * grid and `crispEdges` gives two-value edges again.
 */
export function TownStreets(props: {
  readonly layout: readonly TownSlot[];
  readonly occupied: readonly TownSlot[];
  readonly props: readonly Prop[];
  readonly scale: number;
  /**
   * The town's own airlight, `r,g,b`.
   *
   * The far paving is washed toward it inside the pattern rather than by a
   * scrim over the whole ground group, because what has to come down at
   * distance is the TEXTURE's contrast and not the road's place in the
   * painting. A scrim over the group would take the near plaza with it.
   */
  readonly air: string;
  /**
   * Whether the paved streets, plaza and spurs are drawn at all.
   *
   * False on the terraced backdrops: the painting carries its own road
   * climbing the shelves, and a full-width cobbled plaza polygon laid over a
   * painted near shelf - or a street ruled across a scarp face - is exactly
   * the "tape over the picture" a critic names first. The shadow layer stays;
   * it is what seats a building on the ground it stands on.
   */
  readonly paved: boolean;
}): JSX.Element {
  const plan = useMemo(
    () => townPathPlan(props.layout, props.occupied),
    [props.layout, props.occupied],
  );
  const s = Math.max(1, props.scale);
  // One device pixel, expressed in canvas units. The cobble joint and the
  // paving's edges are drawn at exactly this, so a stone is never 1px on one
  // side of the road and 2px on the other.
  const hair = 1 / s;
  const stones = useMemo(
    () => BANDS.map((b) => ({
      band: b,
      size: COBBLE_SIZE[b],
      cells: cobbleTile(COBBLE_SIZE[b].w, COBBLE_SIZE[b].h, COBBLE_SIZE[b].joint),
    })),
    [],
  );

  /**
   * A single sun, upper left, and one shadow per standing thing.
   *
   * This layer used to carry `mix-blend-mode: multiply`, which is why three
   * rounds of critics reported that no building casts a shadow: the SVG is
   * scaled by a `transform`, a transform is a stacking context, and a blend
   * mode inside one has only its own siblings to blend against - never the
   * painted backdrop underneath. Plain dark fills land on the grass whatever
   * is behind them, so the shadows are simply there.
   *
   * THREE shapes per sprite, and the third is the one a critic was still
   * asking for after the blend-mode fix. Two ellipses give contact - the line
   * that says an object is ON the ground rather than floating over it - but an
   * axis-aligned ellipse under a sprite is a pool, not a cast: it never
   * reaches across the street and it never lands on a neighbour, so "a single
   * sun, upper left" stayed asserted and undemonstrated. The third is a
   * TAPERED PARALLELOGRAM thrown along the sun vector: the base edge is the
   * sprite's own footprint, the far edge is that footprint translated by
   * `SUN_CAST` and pulled in on the shaded side, which is the projection of a
   * box onto a ground plane by a low light. It is a flat fill and not a
   * gradient because this is a hard-edged pixel scene, and it is drawn FIRST
   * so the two contact ellipses sit on top of it and hide the seam where it
   * leaves the wall.
   *
   * The two ellipses are centred on the caster and not beside it. They were at
   * `x + w*0.46` and `x + w*0.36` - half a building's width to the right of
   * the building - which is a contact pool that makes contact with nothing:
   * the ground under the wall was clear and there was a dark ellipse in the
   * street next to it. The cast is what travels along the sun vector; contact
   * does not travel, it is where the object meets the floor.
   */
  const shadows = useMemo(() => {
    const out: Shadow[] = [];
    const cast = (x: number, y: number, w: number): Shadow => {
      const a = w * 0.44;
      const dx = w * SUN_CAST.x;
      const dy = w * SUN_CAST.y;
      return {
        kind: "cast",
        a: shadowStrength(y),
        points: `${String(x - a)},${String(y)} ${String(x + a)},${String(y)} `
          + `${String(x + dx + a * 0.5)},${String(y + dy)} `
          + `${String(x + dx - a * 0.72)},${String(y + dy)}`,
      };
    };
    for (const slot of props.occupied) {
      const w = slot.w * bandScale(slot);
      // The art carries a few transparent rows under its apron, so the blob is
      // lifted off the nominal ground line by a fraction of the sprite's own
      // width. Drawn at the slot's y exactly, it sits in FRONT of the building
      // rather than under it, which is worse than no shadow at all.
      const base = slot.y - w * 0.05;
      const a = shadowStrength(base);
      out.push(cast(slot.x, base, w));
      out.push({ kind: "pool", x: slot.x + w * 0.10, y: base, rx: w * 0.50, ry: w * 0.145, k: 0, a });
      out.push({ kind: "pool", x: slot.x + w * 0.06, y: base, rx: w * 0.30, ry: w * 0.09, k: 1, a });
      // and the line where the walls stand on it, footprint-shaped
      out.push({
        kind: "base", x: slot.x - w * 0.44 + w * 0.05, y: base - w * 0.012,
        w: w * 0.88, h: w * 0.055, a: a * 0.9,
      });
    }
    for (const p of props.props) {
      // A foreground occluder stands nearer than the ground plane this layer
      // draws on and its foot is below the canvas, so a pool for it lands
      // nowhere and a cast for it crosses the whole promenade. It has none.
      if (p.fg) continue;
      // Fifty-eight rather than forty. The prop sprites are 64 and 128 source
      // pixels wide; forty put a 20-canvas-pixel pool under a 32-pixel prop,
      // which is a smudge beside a thing rather than the line where it meets
      // the ground - measured on the shipped Emberpit shot the lava boulder at
      // device (355,880) had NO visible darkening under it at all and was the
      // brightest object in the lower third.
      const w = 58 * p.scale;
      const base = p.y - 2;
      const a = shadowStrength(base);
      out.push(cast(p.x, base, w * 0.8));
      out.push({ kind: "pool", x: p.x + w * 0.08, y: base, rx: w * 0.56, ry: w * 0.19, k: 0, a });
      out.push({ kind: "pool", x: p.x + w * 0.04, y: base, rx: w * 0.30, ry: w * 0.11, k: 1, a });
      // And the seat: a flat, hard-edged ellipse offset down and right on the
      // same 45-degree key the buildings use. The two pools above are radial
      // gradients that fall to nothing at their own rim, so on a small prop
      // there is no row anywhere in them that is unambiguously dark; a solid
      // fill under the foot is the only shape that says CONTACT rather than
      // says haze. Drawn last of the three so it sits on top of both.
      out.push({
        kind: "seat", x: p.x + w * 0.11, y: base + w * 0.035,
        rx: w * 0.38, ry: w * 0.128, a: a * 0.86,
      });
      // A second, tighter one inside it. Four of the twelve towns screen a
      // ground light over the finished frame - Emberpit's lava pools, Greenmantle's
      // shaft - and a screen blend lifts a flat 40% shadow back toward the
      // plane it was cut into, so on those towns one ellipse reads as a
      // smudge. Two concentric ones leave a core the light cannot fill.
      out.push({
        kind: "seat", x: p.x + w * 0.13, y: base + w * 0.045,
        rx: w * 0.19, ry: w * 0.068, a: a * 0.80,
      });
    }
    // Casts under every pool, or a near building's cast is drawn over the
    // contact line of the one it reaches, and the pair reads as a smear.
    return [
      ...out.filter((s) => s.kind === "cast"),
      ...out.filter((s) => s.kind === "pool"),
      ...out.filter((s) => s.kind === "base"),
      ...out.filter((s) => s.kind === "seat"),
    ];
  }, [props.occupied, props.props]);

  return (
    <svg
      className="hs-town__paths"
      width={Math.round(TOWN_CANVAS_W * s)}
      height={Math.round(TOWN_CANVAS_H * s)}
      viewBox={`0 0 ${String(TOWN_CANVAS_W)} ${String(TOWN_CANVAS_H)}`}
      style={{ transform: `scale(${String(1 / s)})`, transformOrigin: "0 0" }}
      shapeRendering="crispEdges"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {stones.map((band) => (
          <pattern
            key={band.band}
            id={`${COBBLE_ID}-${band.band}`}
            width={band.size.w * COBBLE_TILES}
            height={band.size.h * COBBLE_TILES}
            patternUnits="userSpaceOnUse"
          >
            <rect width={band.size.w * COBBLE_TILES} height={band.size.h * COBBLE_TILES}
              className="hs-cob--joint" />
            {band.cells.map((c, i) => (
              <polygon
                key={`c${String(i)}`}
                points={c.points}
                className={`hs-cob--t${String(c.tone)}`}
                strokeWidth={hair * band.size.joint}
              />
            ))}
            {/* Per stone, not per band. One opacity over two hundred stones is
                a second lattice at half the period, which is what a critic
                read as "a dead-uniform isometric diamond lattice"; scaled by
                each stone's own `proud` the lit faces run from a stone worn
                flush to one standing proud of its neighbours, which is what
                a setted road actually does. */}
            {band.size.relief <= 0 ? null : band.cells.map((c, i) => (
              <polygon key={`l${String(i)}`} points={c.lit} className="hs-cob--lit"
                style={{ fillOpacity: 0.34 * band.size.relief * c.proud }} />
            ))}
            {band.size.relief <= 0 ? null : band.cells.map((c, i) => (
              <polygon key={`d${String(i)}`} points={c.dim} className="hs-cob--dim"
                style={{ fillOpacity: 0.28 * band.size.relief * (1.9 - c.proud) }} />
            ))}
            {/* Wear at the scale of one stone: the rut, the puddle, the stone
                the carts have polished dark. One in seven, hashed off the same
                seed as the tone, so it adds no period of its own. */}
            {band.cells.map((c, i) => (c.worn ? (
              <polygon key={`w${String(i)}`} points={c.points} className="hs-cob--worn"
                style={{ fillOpacity: 0.42 }} />
            ) : null))}
            {/* The air, inside the tile: see `Paving.haze`. Last, so it covers
                the stones and their relief together and takes the whole
                texture's contrast down rather than half of it. */}
            {band.size.haze <= 0 ? null : (
              <rect width={band.size.w * COBBLE_TILES} height={band.size.h * COBBLE_TILES}
                fill={`rgb(${props.air})`} fillOpacity={band.size.haze} />
            )}
            {/* ...and the value, which the air cannot carry: see Paving.dim. */}
            {band.size.dim <= 0 ? null : (
              <rect width={band.size.w * COBBLE_TILES} height={band.size.h * COBBLE_TILES}
                fill="#000" fillOpacity={band.size.dim} />
            )}
          </pattern>
        ))}
        {/* Wear at the scale of a cart track, over the per-stone wear inside
            the cobble tile. Nine figures rather than four: at four, a
            167x83 period carried about 4% coverage, and a critic reading the
            Castle plaza at 4x found "near-identical stone tone edge to edge,
            no mortar shadow, no wear". The period shares no factor with any
            band's stone tile, so the two together have no beat. */}
        <pattern id={MOTTLE_ID} width="167" height="83" patternUnits="userSpaceOnUse">
          <polygon points="30,6 74,28 30,50 -14,28" className="hs-cob--worn" />
          <polygon points="126,44 158,60 126,76 94,60" className="hs-cob--worn" />
          <polygon points="8,58 40,74 8,90 -24,74" className="hs-cob--worn" />
          <polygon points="150,4 174,16 150,28 126,16" className="hs-cob--worn" />
          <polygon points="104,3 136,19 104,35 72,19" className="hs-cob--fresh" />
          <polygon points="46,30 70,42 46,54 22,42" className="hs-cob--fresh" />
          <polygon points="62,62 86,74 62,86 38,74" className="hs-cob--wet" />
          <polygon points="118,26 142,38 118,50 94,38" className="hs-cob--wet" />
          <polygon points="86,44 102,52 86,60 70,52" className="hs-cob--wet" />
        </pattern>
        {/* Aerial perspective, on the ground plane, the right way round.
            This ramp used to lift the far end and darken the near one - a
            pale #c7d0bd at the back street and a brown #33291a on the
            promenade - so the far band measured 96.1 luma against the near
            plaza's 82.1 on Greenmantle. Every other depth cue on this screen says
            the near plane takes the light and the distance gives value up
            (see NEAR_STOPS and FarGrade), and the floor under them said the
            opposite. It is the near end that is warm and lit now, and the far
            end that goes cool and dim. */}
        <linearGradient id={DEPTH_ID} x1="0" y1="330" x2="0" y2="726" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#33362e" stopOpacity="0.40" />
          <stop offset="0.45" stopColor="#7c7a6c" stopOpacity="0.10" />
          <stop offset="1" stopColor="#f2dcb0" stopOpacity="0.16" />
        </linearGradient>
        <radialGradient id={SHADOW_ID}>
          <stop offset="0" stopColor="#1c1508" stopOpacity="0.58" />
          <stop offset="0.55" stopColor="#221a0c" stopOpacity="0.44" />
          <stop offset="1" stopColor="#221a0c" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${SHADOW_ID}-core`}>
          <stop offset="0" stopColor="#120c05" stopOpacity="0.86" />
          <stop offset="0.7" stopColor="#120c05" stopOpacity="0.62" />
          <stop offset="1" stopColor="#120c05" stopOpacity="0" />
        </radialGradient>
      </defs>

      {props.paved ? (
      <g className="hs-town__ground">
      {plan.halos.map((v, i) => (
        <polygon key={`h-${String(i)}`} points={polygonPoints(v)}
          className="hs-town__halo" />
      ))}
      {plan.verges.map((v, i) => (
        <polygon key={`v-${String(i)}`} points={polygonPoints(v)}
          className="hs-town__verge-soft" strokeWidth={5} />
      ))}
      {plan.verges.map((v, i) => (
        <polygon key={`k-${String(i)}`} points={polygonPoints(v)}
          className="hs-town__verge" strokeWidth={2} />
      ))}
      {plan.ways.map((way, i) => {
        const pts = polygonPoints(way);
        return (
          <g key={`way-${String(i)}`}>
            <polygon points={pts} className="hs-town__paving"
              fill={`url(#${COBBLE_ID}-${way.band})`} />
            <polygon points={pts} className="hs-town__mottle" />
            <polygon points={pts} className="hs-town__depth" />
            {/* The kerb: a dark cut where stone meets grass. Without it the
                boundary is a one-pixel colour change and the paving reads as
                a polygon fill rather than as a surface with an edge. */}
            <polygon points={pts} className="hs-town__kerb" strokeWidth={2 * hair} />
          </g>
        );
      })}
      </g>
      ) : null}
      <g className="hs-town__shadows">
        {shadows.map((sh, i) => {
          if (sh.kind === "cast") {
            return <polygon key={`sh-${String(i)}`} points={sh.points}
              className="hs-town__cast" opacity={sh.a} />;
          }
          if (sh.kind === "base") {
            return <rect key={`sh-${String(i)}`} x={sh.x} y={sh.y} width={sh.w}
              height={sh.h} rx={sh.h * 0.45} className="hs-town__seat" opacity={sh.a} />;
          }
          if (sh.kind === "seat") {
            return <ellipse key={`sh-${String(i)}`} cx={sh.x} cy={sh.y} rx={sh.rx}
              ry={sh.ry} className="hs-town__seat" opacity={sh.a} />;
          }
          return <ellipse key={`sh-${String(i)}`} cx={sh.x} cy={sh.y} rx={sh.rx}
            ry={sh.ry} opacity={sh.a}
            fill={`url(#${SHADOW_ID}${sh.k === 1 ? "-core" : ""})`} />;
        })}
      </g>
    </svg>
  );
}

// ------------------------------------------------------------- one building

interface TownBuildingProps {
  readonly resolved: ResolvedSlot;
  readonly ground: string;
  readonly blocked: boolean;
  readonly onEnter: (def: BuildingDef, built: boolean) => void;
  readonly onLeave: () => void;
  readonly onOpen: () => void;
}

/**
 * Roof colour, per building family.
 *
 * Every Castle structure ships with the same cobalt slate at the same value,
 * which is why the tavern, the marketplace and the cathedral behind them read
 * as one continuous blue mass rather than as three things a player can click.
 * The classic's Castle mixes red tile, blue slate and bare stone. Repainting twelve
 * sprites is not on the table, so the hue is rotated per family instead: the
 * masonry is near-grey and barely moves, the slate swings a long way, and the
 * trade buildings come out terracotta while the temples stay blue.
 */
const ROOF_TINT: Readonly<Record<string, string>> = Object.freeze({
  tavern: "hue-rotate(-16deg) saturate(1.06) brightness(0.95)",
  marketplace: "hue-rotate(-13deg) saturate(1.04) brightness(1.03)",
  blacksmith: "hue-rotate(-18deg) saturate(1.05) brightness(0.93)",
  special_1: "hue-rotate(-14deg) brightness(0.97)",
  resource_silo: "hue-rotate(-11deg) brightness(1.04)",
  dwelling_1: "hue-rotate(-9deg) brightness(0.96)",
  dwelling_4: "hue-rotate(-10deg) brightness(1.03)",
  dwelling_6: "hue-rotate(13deg) saturate(0.94) brightness(1.02)",
  dwelling_2: "hue-rotate(10deg) brightness(0.97)",
  special_2: "saturate(0.72) brightness(1.05)",
  fort: "saturate(0.80) brightness(1.07)",
});

/**
 * The light a faction's own country throws on its buildings.
 *
 * The paired half of `--hs-bg-tone` in `townChrome`'s stylesheet: the backdrop
 * gives up chroma and contrast, the buildings take a little more of both, and
 * the two together are what separate a Fenmire from the swamp it stands in.
 * It rides on `--hs-roof` because that variable is already this element's own
 * slot in `.hs-town__art`'s filter chain - a second CSS rule would have to
 * redeclare the whole chain, and at a specificity that outranks the hover,
 * focus-visible and ghost rules the chain is shared with.
 *
 * Populated for all eight kinds now, not the swamp alone. The measurement that
 * used to justify "only the swamp needs it" was building-vs-backdrop |dLuma|,
 * and that metric handed out a false all-clear on the town where the failure
 * is worst: Emberpit scored 28 and was left alone as "lit by its own fires
 * against near-black ground", while its foreground band's luma sd is 18.0
 * against the classic Emberpit's 46.8 - the flattest foreground in the twelve,
 * measured against the least flat of the ten references. A difference of means
 * cannot see a flat plane; it only sees whether two flat planes differ.
 *
 * So these are per-COUNTRY, and each one is the amount of chroma and local
 * contrast a building has to take back from the light its own ground throws.
 * The ash and cave entries are the strongest because a fire-lit or
 * crystal-lit plane is all local contrast and no ambient; the snow and desert
 * entries are the weakest because a bright plane already separates.
 */
const GROUND_TONE: Readonly<Record<string, string>> = Object.freeze({
  grass: "saturate(1.06) contrast(1.04)",
  snow: "saturate(1.10) contrast(1.03)",
  ash: "saturate(1.40) contrast(1.26) brightness(1.06)",
  cave: "saturate(1.30) contrast(1.22) brightness(1.10)",
  grave: "saturate(1.18) contrast(1.12) brightness(1.02)",
  rough: "saturate(1.14) contrast(1.08)",
  swamp: "saturate(1.32) contrast(1.14) brightness(1.02)",
  desert: "saturate(1.12) contrast(1.05)",
  // A bright sand plane separates a building on value alone, the way snow
  // does, so the coast asks for about as little as the snow entry.
  coast: "saturate(1.12) contrast(1.04)",
});

/**
 * The frame's tonal grade, per faction, composed into every layer's filter.
 *
 * The one number that separates our painting from the classic's is not its value
 * RANGE - measured over six hand-picked buildings a side of the Fenmire pair,
 * ours 130.6 against 145.1, ten per cent - it is where that range SITS. Our
 * paintings never reach black: p1 luma 18-22 in every daylit town against
 * The classic's 0.6-13.9, and a dark-pixel fraction of 7.0% against Saltreach's 25.4% and
 * 13.8% against Fenmire' 37.8%. The buildings already have the contrast; the
 * whole frame is exposed about a stop and a half hot with the shadow end
 * filled in.
 *
 * It is set as a CSS variable on the canvas rather than applied here, because
 * four layers have to take the same grade or the backdrop, the ground, the
 * scenery and the buildings drift apart into four different times of day:
 * `.hs-town__bg`, `.hs-town__ground`, `.hs-town__prop .hs-town__art` and this
 * element's own `--hs-roof`. The values live in `townChrome`'s stylesheet
 * beside the per-ground tints they compose with; what lives here is the
 * composition into the building's own chain.
 *
 * Castle is deliberately absent from that table and must stay absent: against
 * Alexandretta it is already at parity (local range 126.1 vs 124.9, dark 7.30%
 * vs 6.01%, bright 6.74% vs 7.24%), and grading a picture that already matches
 * only moves it off the target.
 */
const GRADE_VAR = "var(--hs-grade, brightness(1))";

/**
 * Aerial perspective, as a filter on the sprite itself.
 *
 * This is the SMALL half of the depth cue. The large half is `AirScrim` below,
 * and the split is not arbitrary: a filter can only rescale a sprite's own
 * colours, and what atmosphere actually does is ADD the sky's colour in front
 * of the object - `out = obj*(1-a) + air*a`. A filter cannot add a hue that is
 * not already in the pixel, so no chain of saturate/contrast/brightness can
 * turn a green hill blue with distance, which is exactly the shift the classic
 * has and this screen did not.
 *
 * What is left here is the part a filter is right for: the far band gives up a
 * little of its own chroma. The brightness lift and most of the contrast cut
 * are GONE. Measured over the horizon band against the eight references with a
 * visible sky, the classic's distant band keeps its local contrast (mean far/near
 * 1.02) and ours was already down at 0.79, so cutting contrast further was
 * pushing a number that had overshot; and our distant band already sits 31
 * luma above our foreground against the classic's 9, so lifting it was making the
 * one gap that is worst worse.
 */
export function haze(band: TownSlot["band"]): string | undefined {
  const h = BAND_HAZE[band];
  if (h <= 0) return undefined;
  return `saturate(${String(1 - 0.22 * h)}) contrast(${String(1 - 0.04 * h)}) `
    + `brightness(${String(1 + 0.02 * h)})`;
}

/**
 * The colour and strength of a town's own air.
 *
 * The one defect a blind art director named across all twelve towns: "the
 * distant band sits at near-foreground saturation... The classic goes grey and
 * blue as it recedes. You don't." Measured on the strip of painting directly
 * under the sky - the true distant band, found per column so a foreground
 * tower cannot donate its own foreground to the far average - against a
 * foreground third:
 *
 *   chroma far/near   the classic, 8 refs with a sky   mean 0.752, max 0.96
 *                     ours, 11 towns with a sky  mean 1.116, median 1.014
 *
 * Six of our eleven sat above the classic's MAXIMUM. That is the tell, and it is a
 * rendering fact rather than an art fact: nothing was standing between the
 * painted hills and the camera.
 *
 * `alpha` is the wash over the backdrop, and it is per FACTION rather than
 * global on purpose. The set's tonal spread is a hard-won property and one
 * atmospheric filter applied twelve times would erode it - so each town gets
 * the amount its own reference asks for, and the amounts run 0.06 to 0.32.
 * Prismgate and Frostmark measured at or below the reference median before any of
 * this and take almost nothing; Gravewatch and Saltreach measured furthest away and
 * take the most.
 *
 * `rgb` is NOT the town's sky colour. Airlight desaturates only if it is less
 * chromatic than what it covers, and a classic sky is a saturated blue; washing
 * a green hill with saturated blue trades one chroma for another. It is the
 * near-neutral, slightly cool grey the sky's light scatters to, and its VALUE
 * is chosen per town to move that town's far band toward its own reference's
 * far/near luma gap: our distant band averaged 49 luma above our foreground
 * where the classic's eight measurable references average 8, so most of these are
 * DARKER than the band they cover, not lighter. Gravewatch is the extreme -
 * ours sat 71 luma above its foreground, Ghostwind's sits 20 BELOW - and takes
 * the darkest air.
 */
interface Airlight {
  /** `r,g,b` of the air, ready to interpolate into an `rgba()`. */
  readonly rgb: string;
  /** How much of it covers the backdrop, 0..1. */
  readonly alpha: number;
}

export const AIRLIGHT: Readonly<Record<string, Airlight>> = Object.freeze({
  // 1.02 measured against Alexandretta's 0.87 and Caryatid's 0.92. The
  // smallest correction of any daylit town, because Castle is the one screen
  // already at parity and the brief on it is not to move it far.
  //
  // Warm rather than cool now, and against the theory rather than with it.
  // Atmospheric scattering is blue and the first draft of this table reasoned
  // from that; the references do not agree. Measured on the strip of painting
  // under the sky, R-B: Alexandretta +37.3, Caryatid +40.5, Kruber +38.3,
  // Seiliedh +15.2, Tirith +10.4. Ours ran +16.2 (Castle), -1.9 (Tower),
  // -1.9 (Saltreach), -4.3 (Frostmark) - neutral to cool where the classic's is warm, and
  // about twenty points short across the daylit set. The SKY is the other way
  // about and stays that way: Alexandretta's sky is R-B -66 at the top and
  // -15 at the horizon, so what the classic has is a cool sky over a warm distance,
  // which is haze lit by a low sun rather than Rayleigh blue. Each rgb below
  // keeps the luma it was fitted to within about a point and turns its hue
  // over; the chroma stays inside the 16 the wash is allowed, because an air
  // as chromatic as the paint trades one chroma for another and moves nothing.
  // The bulk of the warmth is `WARMTH` below - an alpha this small cannot
  // carry twenty points of hue however it is coloured.
  sunspire: { rgb: "133,127,120", alpha: 0.14 },
  // Seiliedh's 0.54 is the deepest haze in the reference set and ours was
  // 1.07. Capped well short of the fit: matching 0.54 needs a wash near 0.46,
  // which is a veil rather than air. It lands at 0.84.
  greenmantle: { rgb: "80,74,67", alpha: 0.30 },
  // Neutral, and the only reason it is neutral is measured: at a cool air
  // Tower's distance lost luma and kept chroma (44.2 -> 43.0 at alpha 0.20)
  // because a blue wash over blue-white snow is IN PHASE with the paint under
  // it and adds back the chroma it removes. Aerial perspective shifts toward
  // the sky's hue only where the distance is warm; where it is already cool it
  // shows up as chroma and contrast lost, not as more blue.
  // Neutral means NEUTRAL, and this entry was 108,101,95 - R-B +13, a warm
  // grey. Over pure-white backdrop snow at alpha 0.26 that is only three
  // points of warmth on its own, but it composes with `WARMTH` below and the
  // pair took Tower's snowfield to a measured 213,201,187: an ice faction
  // standing on sand. Cool by 9 rather than warm by 13, which is the sign the
  // note above actually argues for.
  skyreach: { rgb: "99,103,110", alpha: 0.26 },
  // A token amount, and the reason is that Emberpit does not have the defect.
  // Firebrand has no sky, so the whole-frame band ratio is the only measure
  // available, and on that measure the reference goes the OTHER way - 1.41,
  // its distance more chromatic than its foreground, because the air in it is
  // lit by the ground - while ours already measured 0.80. A first draft at
  // 0.18 was fitted to a stale 2.46 read off the round-opening baseline, and
  // it was moving the one town in the set away from its own reference.
  // 0.08 was fitted to Firebrand's whole-frame band ratio, which is the
  // measure that says Emberpit does not have the chroma defect - and it does
  // not. It has the ORDERING defect: measured over the far street against the
  // promenade the distance is 46.4 and the promenade 34.2, so the far ground
  // is the brighter of the two. Its near light is pinned at zero and its far
  // grade is capped by townChrome's paired saturate, so the air is the only
  // lever left. The rgb is the town's own soot, so what this adds is value
  // rather than a second hue.
  emberpit: { rgb: "43,36,29", alpha: 0.22 },
  // The largest correction in the set: 1.14 against Ghostwind's 0.59, and a
  // far band 71 luma above its own foreground where Ghostwind's sits 20 below.
  gravewatch: { rgb: "45,50,59", alpha: 0.32 },
  // Darkhold has no sky either, so this is fitted to the whole-frame measure:
  // Darkhold 0.82 against our 0.93. It lands at 0.83.
  // Neutral for the same reason as Tower: a violet air over a violet cavern
  // is in phase with it.
  deepvault: { rgb: "41,41,43", alpha: 0.22 },
  // Kruber 0.96 against our 1.33 on the whole-frame measure - the widest gap
  // of any town with a faction-matched reference.
  ironhorde: { rgb: "96,88,82", alpha: 0.26 },
  // The one town whose distance was ALREADY over-desaturated before any of
  // this: 0.53 against Edgewater's 0.65, because the swamp's --hs-bg-tone in
  // townChrome already takes the backdrop to saturate(0.52). At 0.24 this took
  // it to 0.42 and out the bottom of the reference range, so it is cut to a
  // token amount - what Fenmire still wants is its far band's LUMA down (52
  // above its own foreground against Edgewater's 11), and that is a grade, not
  // an atmosphere.
  // Lighter than the band it covers now rather than darker, and that is what
  // an air behind a silhouette is for: the ruins and the temple in Fenmire'
  // distance are the dark shapes, so raising the ground behind them is what
  // separates them, where taking the whole band down together is what buried
  // them. It stays a token amount - the swamp's --hs-bg-tone in townChrome
  // already takes its backdrop to saturate(0.52), which is why this town was
  // the one already over-desaturated in the distance before any of this.
  fenmire: { rgb: "104,101,92", alpha: 0.14 },
  // Was a token 0.08, on the reading that Prismgate's far/near chroma was
  // already past Styriam's. It is the town's TOP FIFTH that pays for this
  // now: at 0.08 Prismgate measured the steepest top-to-bottom luma ramp of the
  // twelve - 1.79 over the painting and 1.96 over the critic's band, against
  // the eleven references' mean of 1.25 - and it takes no far grade, so the
  // wash is the only lever its distance has. A heavier and DARKER air is what
  // brings the distance down. It costs chroma: 0.64 far/near before, and a
  // wash from 0.08 to 0.24 scales that by (1-0.24)/(1-0.08). That lands near
  // the bottom of the reference range rather than outside it, which is the
  // side to miss on when the alternative is the steepest ramp in the set.
  // Darker and heavier again, for the reason given on DEPTH.prismgate: this
  // town takes no far grade at all, so its air is the only thing that can
  // bring its distance down, and its distance measured 105.7 against a
  // promenade at 90.9. 0.29 rather than 0.32 because a test holds Prismgate,
  // Cogworks and Frostmark - the three that measured at or under the reference
  // median before any wash - strictly under Gravewatch, Saltreach and Greenmantle.
  prismgate: { rgb: "76,72,68", alpha: 0.29 },
  saltreach: { rgb: "102,95,89", alpha: 0.32 },
  // No faction-matched reference ships for these two, so the target is the
  // reference population's median far/near chroma - 0.813 on the horizon band,
  // 0.834 on the whole frame. They part company there: Cogworks measured 1.13
  // and needs a real wash, Frostmark measured 0.67 and is already past it, so
  // Frostmark's exists only to bring its far band's luma down.
  cogworks: { rgb: "97,91,85", alpha: 0.18 },
  // Same story as Prismgate and for the same reason: 0.06 was fitted to a
  // far/near chroma of 0.69 that was already past the reference median, and
  // it left the top fifth at 139.8 against the reference population's 94.8 -
  // a 2.02 ramp on the critic's band, second worst of the twelve. Its far
  // grade is on its cap and the cap is paid for in `townChrome`, so the air
  // is the lever that is left.
  frostmark: { rgb: "76,81,88", alpha: 0.24 },
  // The thirteenth, and it is here rather than on `AIR_DEFAULT` for the
  // reason the test states: a faction that falls through gets somebody else's
  // weather. Blackwater's backdrop is a drowned tidal flat at dusk - measured
  // over its own art, a treeline horizon at row ~200, water at luma 38 under
  // it and a foreground flat at luma 131, so its distance is already DARKER
  // than its foreground and what it needs is chroma out of the far reeds
  // rather than value. A modest, dead-neutral wash.
  blackwater: { rgb: "70,72,74", alpha: 0.16 },
});

const AIR_DEFAULT: Airlight = Object.freeze({ rgb: "95,100,109", alpha: 0.12 });

export const airlight = (faction: string): Airlight => AIRLIGHT[faction] ?? AIR_DEFAULT;

/**
 * The other end of the same measurement: how the near plane is lit, and how
 * far the distance comes down.
 *
 * The wash above closed the far/near CHROMA gap and left the LUMINANCE one
 * open, and a final art director measured what that costs. Top-fifth over
 * bottom-fifth mean luma, over the painting between the top bar and the
 * recruitment tray:
 *
 *   our twelve towns           mean 2.07, range 1.08 - 2.79
 *   eleven the classic town screens  mean 1.25, range 0.80 - 1.83
 *   our Castle 2.52            the classic Castle 1.49 and 1.49
 *
 * The diagnosis is exact and it is not the one the wash was built for: "real
 * aerial perspective lifts and desaturates the distance; this darkens the
 * foreground instead." Read per town against its own reference, both ends are
 * off and they are off in opposite directions - our Castle's top fifth is 20%
 * hotter than Alexandretta's and its bottom fifth is 12% darker; our Tower's
 * bottom fifth is 30% darker than Tirith's. So there are two numbers per town
 * and each is fitted to the same reference the airlight was fitted to.
 *
 * `near` is a warm light SCREENED onto the near plane, and screen is chosen
 * over an alpha wash for a measured reason: our foregrounds do not lack mean
 * luma so much as they are full of holes. Castle's bottom fifth ran 21.9%
 * below L=36 against Alexandretta's 9.6%, and screen lifts a black by the
 * whole of the light and a highlight by almost none, which is the shape of
 * that error. It cannot clip, which an additive light or a dodge both can -
 * Tower's foreground snow already sits 12% above L=200.
 *
 * `far` is a black MULTIPLY over the distance, and multiply rather than
 * another airlight because the airlight is finished: the wash is an alpha
 * blend toward a fixed grey, and more of it trades the paint's own hue for a
 * neutral one, which is the trade the chroma measurement says is already
 * done. A multiply scales all three channels by one factor, so it takes value
 * out of the distance and leaves its HUE and its saturation exactly where the
 * wash put them.
 *
 * It does not leave its absolute chroma there, and that distinction cost a
 * round to learn. `max-min` scales with the multiply like everything else, so
 * a far band multiplied by 0.833 measures 13% less chroma however unchanged
 * its saturation is - and far/near chroma is the ratio the whole previous
 * round was fitted on. Shot without a compensation the set came back at 0.664
 * against the classic's 0.752, having started at 0.785: an overshoot, not a
 * regression, but an overshoot of the one number that was right. The
 * compensation is a `saturate()` on the backdrop in `townChrome`, one per
 * town at exactly `1/(1-far)`, and it is on the BACKDROP rather than here
 * because a filter cannot be applied to a blend scrim - see the note there.
 *
 * Both are capped - `near` at 0.70, `far` at 0.18 - and the near cap has
 * moved. It was 0.42, and 0.42 was not a taste judgement either: shot at
 * 0.90, which is what fitting Ironhorde's foreground to Kruber's asks for,
 * the two standing stones in its near ground came back pale blue-white - a
 * gamma lift takes a light grey granite at L=153 to L=193 - and read as ice
 * pillars in a red desert. That was a fault in the ASSET, not in the light:
 * `obs_standing_stone` measures R-B +0.1 over its opaque pixels, a dead
 * neutral granite, and a neutral lifted inside a warm frame reads cold by
 * simultaneous contrast whatever the light is made of. `FOREGROUND.rough` is
 * a warm rock now - `obs_mesa_rock`, R-B +68.9 - so the cap that was paid for
 * by that one sprite is no longer paid for by anything, and the three towns
 * whose ramp is worst are exactly three of the five that sat on it.
 * Fitting Gravewatch' distance exactly means matching Ghostwind, whose
 * distance is DARKER than its own foreground; fitting Fenmire' exactly means
 * a swamp lit like a lawn. A cap leaves them short of their reference and
 * inside the reference population's range, which is the right side to miss
 * on.
 */
interface Depth {
  /** How much of the near light the promenade takes, 0..1. */
  readonly near: number;
  /** How much luma the distance gives up, 0..1. */
  readonly far: number;
}

/**
 * The near light's colour, one for all twelve.
 *
 * A low sun on the plane nearest the camera, and it is deliberately NOT per
 * town the way `AIRLIGHT.rgb` is. The air's colour has to be per town because
 * a wash trades the paint's chroma for its own and the trade is only neutral
 * when the two are in phase; a screen adds light without taking any, so the
 * only thing the colour decides is how warm the near ground reads. One warm
 * daylight across the set, and the amount is the single number per screen the
 * critic asked for.
 */
const NEAR_RGB = "255,226,186";

export const DEPTH: Readonly<Record<string, Depth>> = Object.freeze({
  // Alexandretta 131.2/87.9, ours 157.5/77.7. Both ends move, and neither is
  // on a cap: it is the only town in the set that lands on both of its
  // reference's bands exactly.
  sunspire: { near: 0.341, far: 0.167 },
  // Seiliedh's foreground is 74.4 and ours is 73.0 - the one town whose near
  // plane was already right. Almost all of its correction is the distance.
  // Both ends move away from that fit on purpose. Measured on the shipped
  // shot over the far street (device rows 352-392) against the promenade
  // (860-915), Greenmantle's distance is 102.7 against 94.6 - the far ground
  // BRIGHTER than the near, which inverts every other depth cue on the
  // screen. Seiliedh's foreground mean is the number this was fitted to and
  // it is the wrong number to hold when the ordering is wrong.
  greenmantle: { near: 0.200, far: 0.160 },
  // Tower's top fifth is RIGHT - 173.8 against Tirith's 172.0 - and its
  // bottom fifth is 110.2 against Tirith's 138.3. Every point of its ramp is
  // at the near end, which is why it read 1.57 at 1080p and 2.20 at laptop:
  // the critic's lower bound lands further into the recruitment tray at the
  // shorter viewport, so a town with a bright distance is punished twice for
  // one dark foreground. Off the old cap at 0.66; the lift is soft-light, so
  // its snow cannot clip and its holes stay holes.
  // Its near end is already on Tirith's; 131.6 against 122.1 on the far/near
  // pair is all at the far end, so that is the end that moves.
  skyreach: { near: 0.660, far: 0.112 },
  // Firebrand's ramp is 1.06 and ours measured 1.00 before any of this, so
  // Emberpit never had the top-to-bottom defect. It has the OTHER one: 46.4
  // against 34.2 on the far/near pair, its distance brighter than its
  // promenade. Its near light stays at zero and a test pins it there, so the
  // whole correction is at the far end - the far grade to the top of the
  // window townChrome's paired saturate(1.06) leaves open, and the rest in
  // its own air.
  emberpit: { near: 0.000, far: 0.096 },
  // Ghostwind's distance is 46.5 and ours is 105.6 - 2.3x, the widest gap of
  // the twelve, and it is at the far end rather than the near one. Capped.
  gravewatch: { near: 0.345, far: 0.18 },
  // Darkhold 50.0/45.8 against our 55.3/39.8. The smallest correction of the
  // twelve, which is the second time Deepvault has measured close to its
  // reference before anything was done to it.
  deepvault: { near: 0.203, far: 0.096 },
  // Off the old cap because its foreground got darker on purpose: its two
  // occluders were light grey granite lifted to L=193 and are warm sandstone
  // now, which took the bottom fifth from 90.6 to 83.9 against Kruber's
  // 106.5. The dark fraction went the right way with it - 6.39% to 7.96%
  // against Kruber's 9.84% - so there is room for the light the mean wants.
  ironhorde: { near: 0.540, far: 0.137 },
  // Edgewater 62.2/60.3 against our 106.9/39.0. Capped at the far end, and it
  // is the town the cap costs most: the swamp's backdrop filter is what puts
  // its far band at the lowest chroma of the twelve, so the saturate() in
  // `townChrome`'s --hs-bg-tone comes back up as this comes down. Value
  // separates the cathedral from the fog now; chroma could not.
  // Off the cap, and DOWNWARD, which is the only entry here that moves that
  // way. "Fenmire backdrop value range is compressed to unreadable mud - the
  // far half sits in a ~20-luma band of olive-grey with no silhouette
  // separation; the temple and the ruins are barely distinguishable from the
  // fog." A multiply compresses: it scales every value in the band by one
  // factor, so a distance already 24 luma BELOW its own foreground (66.8
  // against 90.6, measured) loses the range it needed to keep two or three
  // readable steps. Aerial haze is right for this town and a value crush is
  // not the same thing.
  // It comes off the cap by as much as it can: the far grade owes its
  // backdrop saturate(1/(1-far)) and townChrome's swamp rule says 1.22, so
  // anything under 0.146 breaks the pair. The rest of the opening is in the
  // air below, which is now LIGHTER than the band it covers.
  fenmire: { near: 0.420, far: 0.150 },
  // Styriam's distance is 150.4 and ours is 142.4 - already under it, so
  // Prismgate is the one town that takes no far grade at all, and it stays that
  // way: a far grade owes its backdrop a saturate of 1/(1-far) in
  // `townChrome`, and Prismgate has no such rule to pair with. So its ramp is
  // corrected from the near end and from its air. Read against its OWN
  // reference Prismgate was never wrong - 1.79 against Styriam's 1.81 - and
  // read against the eleven references' mean of 1.25 it was the steepest in
  // the set. This lands it between the two rather than on either.
  // 105.7 against 90.9 is the worst far/near inversion of the twelve, and a
  // town that takes no far grade has nothing but its air to answer it with.
  // So it takes one now - and it is the one entry in this table with no
  // paired saturate() in townChrome, which this file does not own, so its
  // backdrop is left about 9% under-compensated on chroma. That is the
  // smaller error: an over-saturated distance still reads as distance and a
  // distance brighter than the foreground does not.
  // 105.7 against 90.9 is the worst far/near inversion of the twelve, and
  // Prismgate is the one town in the set that cannot answer it with a far
  // grade: a far grade owes its backdrop a saturate of 1/(1-far) in
  // townChrome, Prismgate has no such rule to pair with, and a test holds it at
  // zero for exactly that reason. So the correction is split between the near
  // light, which comes up onto its cap, and its own air, which comes down.
  prismgate: { near: 0.700, far: 0.000 },
  saltreach: { near: 0.267, far: 0.034 },
  // No faction-matched reference for these two, so the target is the eleven
  // references' own mean band pair, 94.8 over 77.4.
  cogworks: { near: 0.420, far: 0.18 },
  // Frostmark's bottom fifth measured 83.7 against that 77.4 and its top fifth
  // 139.8 against 94.8, so on the population target it is the distance that
  // is wrong. But Frostmark is a snowfield town and the only snow reference in
  // the set is Tirith, whose own foreground is 138.3 - so the correction is
  // split: the far grade is on its cap and cannot move without a `townChrome`
  // rule this file does not own, the air takes the distance down, and the
  // near light takes the foreground up.
  frostmark: { near: 0.580, far: 0.18 },
  // Measured off its own backdrop: top fifth 94.5, bottom fifth 130.9, a ramp
  // of 0.72 where the eleven references average 1.25. Blackwater is the one
  // town in the set whose distance is already darker than its foreground, so
  // it takes no far grade at all - and taking none is also what keeps it off
  // the `--hs-bg-tone` saturate rule it would otherwise owe its backdrop,
  // which lives in `townChrome` and not in this file.
  blackwater: { near: 0.120, far: 0.000 },
});

const DEPTH_DEFAULT: Depth = Object.freeze({ near: 0.30, far: 0.10 });

export const depth = (faction: string): Depth => DEPTH[faction] ?? DEPTH_DEFAULT;

/**
 * How warm the distance is, per town.
 *
 * The air above is a VALUE correction that happens to have a hue. This is the
 * hue correction, and it is separate because the two cannot be carried by one
 * scrim: the air's alphas run 0.06 to 0.32 and an alpha blend at 0.14 cannot
 * move a band's R-B by twenty points however it is coloured, while an alpha
 * heavy enough to do it would take the town's distance to the air's own value
 * and undo the fit.
 *
 * Measured on the strip of painting directly under the sky, mean R minus mean
 * B, which is a colour temperature rather than a luminance - reading one for
 * the other is how a previous round reported an overshoot on Ironhorde that
 * does not exist:
 *
 *   The classic   Alexandretta +37.3  Caryatid +40.5  Kruber +38.3
 *           Seiliedh     +15.2  Tirith   +10.4  Styriam   -0.4
 *   ours    Castle       +16.2  Tower     -1.9  Saltreach      -1.9  Frostmark -4.3
 *
 * So the daylit distance is about twenty points short of warm, and Styriam is
 * the reference that says this is a per-town number and not a global one:
 * Prismgate's own reference has a COOL distance, and Prismgate measured -2.0
 * against its -0.4, so Prismgate takes none of this. Neither do the four towns
 * with no daylight in them at all - Emberpit, Gravewatch, Deepvault and the
 * caverns generally have no reference with a sky and no sun to scatter.
 *
 * Ironhorde's number is the one that is not measured this way, and the
 * reason is the trap this file has sprung once already: the horizon band is
 * found with a sky classifier that asks for `b >= r - 6`, Ironhorde's sky is
 * warm, and the classifier selects nothing at all - it returned 0.00% of the
 * painting on our Ironhorde shot. Its number is Kruber's +38.3 read across,
 * not a fit, and it is flagged here so the next reader does not quote it as
 * one.
 */
export interface Warmth {
  /** How much of the low sun's colour the distance takes, 0..1. */
  readonly amount: number;
  /**
   * The canvas row the warmth starts on: this town's own horizon.
   *
   * Measured twice per town and the deeper of the two taken. The first is the
   * backdrop sprite's own first ridge - the row where the smooth run down
   * from the top of each column ends, median over 320 columns, which is
   * hue-agnostic and so works on Ironhorde's warm sky where the metric's
   * classifier does not. The second is where the metric's own sky mask ends
   * on the shipped shot, which runs deeper on the snow towns because pale
   * snowfields answer to `b >= r - 6 && L > 60` exactly as sky does. Starting
   * on the deeper of the two is what keeps the blue out of the warmth:
   *
   *   town        ridge   mask    used
   *   castle        169    181     181
   *   tower         154    234     234
   *   cove          117    168     168
   *   frostmark     135    215     215
   *   ironhorde     114     --     114   (warm sky; the mask finds nothing)
   */
  readonly from: number;
}

export const WARMTH: Readonly<Record<string, Warmth>> = Object.freeze({
  sunspire: { amount: 0.36, from: 181 },
  greenmantle: { amount: 0.07, from: 128 },
  // 0.19 and 0.26, and they were the two numbers in this table fitted to a
  // population rather than to a reference. `color` REPLACES hue and
  // saturation in proportion to its alpha, so a fifth of a 255,200,150 over
  // white snow is a fifth of an orange: Tower's backdrop snow measured
  // 213,201,187 and its blue plateau stone went from B-R +66 to -5. Tirith is
  // the one snow reference in the set and its distance measures R-B +10.4,
  // the coolest daylit reference there is. A token amount holds that without
  // painting the snowfield beige.
  skyreach: { amount: 0.05, from: 234 },
  emberpit: { amount: 0, from: 167 },
  gravewatch: { amount: 0, from: 196 },
  deepvault: { amount: 0, from: 80 },
  ironhorde: { amount: 0.32, from: 114 },
  fenmire: { amount: 0.16, from: 200 },
  prismgate: { amount: 0, from: 191 },
  saltreach: { amount: 0.20, from: 190 },
  cogworks: { amount: 0.25, from: 169 },
  frostmark: { amount: 0.05, from: 215 },
  // Its horizon is the treeline at canvas row 205, and the sky over it is a
  // low olive dusk that already measures R-B +27 - warm, but by its own
  // light rather than by a scrim, so it takes a token amount.
  blackwater: { amount: 0.10, from: 205 },
});

const WARMTH_DEFAULT: Warmth = Object.freeze({ amount: 0, from: 180 });

export const warmth = (faction: string): Warmth => WARMTH[faction] ?? WARMTH_DEFAULT;

/**
 * The colour the low sun scatters to, one for all twelve.
 *
 * Unlike `AIRLIGHT.rgb` this one is allowed to be a colour rather than an air,
 * because the blend it is laid in takes only its hue and its saturation and
 * leaves the paint's own luminance alone - so it cannot trade chroma for
 * value the way a wash does, and there is nothing to fit per town but how
 * much of it there is.
 */
const WARM_RGB = "255,200,150";

/**
 * Where the warmth starts, and why it is not the air's own table.
 *
 * The air is full strength at canvas row 0 because the backdrop is farthest
 * at the top of the frame. Warmth cannot be, because the top of the frame is
 * SKY, and the classic's sky is the one part of its picture that is not warm:
 * Alexandretta's runs R-B -66 at the top of the sky to -15 at the horizon,
 * Kruber's -94 to -7. Ours is already the less blue of the two - Castle's
 * measures -32 over the whole mask - so a scrim that warmed the sky would
 * push the one band that is nearest its reference away from it. Shot with one
 * global start row at 140 it did exactly that: Frostmark's sky came back at
 * R-B +0.9, a warm sky over a snowfield.
 *
 * So the start row is the town's OWN horizon, and the ramp is short - forty
 * canvas rows, about a third of the far band's depth - so that the band is at
 * or near full strength over most of itself while the sky above the horizon
 * takes none of it.
 */
const WARM_RAMP = 40;

export function warmStops(from: number, rows: AirRows = SHARED_ROWS): readonly (readonly [number, number])[] {
  return [
    [0, 0],
    [from, 0],
    [Math.min(from + WARM_RAMP, rows.full), 1],
    [rows.full, 1],
    [rows.half, AIR_HALF_AMOUNT],
    [rows.clear, 0],
  ];
}

/** The shared table's rows, which the exported stop tables below are built on. */
const SHARED_ROWS: AirRows = Object.freeze({
  full: AIR_FULL_Y, half: AIR_HALF_Y, clear: AIR_CLEAR_Y, split: AIR_SPLIT_Y,
});

/** The air's stop table for one faction's rows; `AIR_STOPS` is the shared table's. */
export const airStops = (rows: AirRows): readonly (readonly [number, number])[] => [
  [0, 1], [rows.full, 1], [rows.half, AIR_HALF_AMOUNT], [rows.clear, 0],
];

/** The near light's stop table for one faction's rows; `NEAR_STOPS` is the shared table's. */
export const nearStops = (rows: AirRows): readonly (readonly [number, number])[] => [
  [rows.full, 0], [rows.half, 1 - AIR_HALF_AMOUNT], [rows.clear, 1], [TOWN_CANVAS_H, 1],
];

/**
 * How much of the wash survives at a given canvas row, as `[y, amount]`.
 *
 * This IS the gradient - `AirScrim` renders these stops and nothing else - so a
 * test that reads this table is reading what the browser paints rather than a
 * second copy of it. The three rows are the three bands' own ground lines, and
 * the last one being zero is what keeps the promenade out of the haze.
 */
export const AIR_STOPS: readonly (readonly [number, number])[] = Object.freeze(airStops(SHARED_ROWS));

/**
 * The same three ground lines, read from the other end.
 *
 * The near light is the air's mirror and it is anchored on the same rows on
 * purpose: at every row either table names, the two come to exactly one. That
 * is what makes distance and the near plane ONE gradient rather than two
 * filters that happen to be switched on at once, and a test reads it back.
 *
 * It runs on past the front band's own floor at full strength because the near
 * plane does not end at the front band - the socket's occluders stand at 740
 * to 800, below a canvas that ends at 720 - and because the strip of promenade
 * between the front doors and the frame is the one place a player reads true
 * scale. It is the last thing on the screen that should be in shadow.
 */
export const NEAR_STOPS: readonly (readonly [number, number])[] = Object.freeze(nearStops(SHARED_ROWS));

/**
 * Where the painter's order is cut for the second wash.
 *
 * `items` is already sorted by ground line, so everything before the index is
 * at or above `AIR_SPLIT_Y` and everything after is below it: the slice moves
 * the wash and can never reorder two sprites.
 */
export const airSplitIndex = (items: readonly { readonly y: number }[], split: number = AIR_SPLIT_Y): number => {
  const at = items.findIndex((item) => item.y > split);
  return at < 0 ? items.length : at;
};

/**
 * How much of the wash the back band takes, against the backdrop's whole.
 *
 * The backdrop is farther than the back band, so it must take strictly more.
 * Two washes are laid rather than one: the first over the backdrop alone, the
 * second over the backdrop AND the back band's buildings and trees. The back
 * band therefore sees this fraction and the backdrop sees the pair composited.
 */
const AIR_BACK_SHARE = 0.45;

/** The wash the back band takes. */
export const airBackAlpha = (alpha: number): number => alpha * AIR_BACK_SHARE;

/**
 * The wash the backdrop takes FIRST, so that the two together come to `alpha`.
 *
 * Two washes at a and b do not come to a+b - they come to 1-(1-a)(1-b) - so
 * the first one is solved for rather than halved, and the table's number is
 * then the real amount of air in front of the painted hills.
 */
export const airFarAlpha = (alpha: number): number => {
  const back = airBackAlpha(alpha);
  return back >= 1 ? alpha : 1 - (1 - alpha) / (1 - back);
};

/**
 * One wash of air, laid between two depth planes.
 *
 * Vertical, because distance on this screen is a function of the canvas row:
 * full down to the back band's ground line, a third of it at the middle band's,
 * gone by the front band's. That last stop is what keeps the promenade - the
 * one place on the screen where the player's eye rests and where true scale is
 * read - out of the haze entirely, and it is also why this is a gradient and
 * not the flat `--hs-bg-tone` filter it joins: the backdrop art runs from the
 * horizon down past the middle band, and a flat filter over it hazes the near
 * grass exactly as much as the far ridge, which is the mistake that produced
 * the false all-clear on Emberpit's foreground.
 */
export function AirScrim(props: { readonly rgb: string; readonly alpha: number; readonly rows: AirRows }): JSX.Element | null {
  return (
    <DepthScrim className="hs-town__air" rgb={props.rgb} alpha={props.alpha} stops={airStops(props.rows)} />
  );
}

/**
 * How much luma the distance gives up, laid where the air is laid.
 *
 * A black multiply rather than more airlight, and the reason is the one
 * measurement in this screen that came out right. The wash is
 * `out = paint*(1-a) + air*a`: every extra unit of it trades the paint's own
 * chroma for the air's, and the far/near chroma ratio is already at the classic's
 * 0.752. Multiply scales all three channels by the same factor, so the
 * distance loses value and the ratio does not move.
 *
 * It is cut into the painter's order at exactly the same index as the second
 * wash, and for the same reason: this is a DISTANCE grade, and a middle-band
 * tower whose roof happens to reach canvas row 200 is not distant. Applied
 * over the finished frame instead, the fort - the tallest thing on the screen
 * and dead centre - would take a value ramp down its own spire.
 */
export function FarGrade(props: { readonly amount: number; readonly rows: AirRows }): JSX.Element | null {
  return (
    <DepthScrim className="hs-town__far" rgb="0,0,0" alpha={props.amount} stops={airStops(props.rows)}
      blend="multiply" />
  );
}

/**
 * The low sun in the distance, as a hue and nothing else.
 *
 * `mix-blend-mode: color` is chosen for one property and it is the property
 * that made this safe to add at all: the CSS non-separable blends run
 * `SetLum(Cs, Lum(Cb))`, so the result carries the BACKDROP's luminance
 * exactly. Six of this screen's twelve towns are frozen on a luma measurement
 * - five ramps and Castle's dark fraction - and a warm alpha wash or a warm
 * multiply would have moved every one of them. Run over a shipped shot before
 * it was written, this shifts Castle's far band from R-B +16.2 to +37.2 and
 * its top-fifth mean luma by 1.5 in 134.7, which is the clipping term and
 * nothing else.
 *
 * What it does cost is far-band SATURATION, because `color` takes the scrim's
 * saturation as well as its hue. That is the one number to watch when this
 * table moves: far/near chroma over the eight references with a sky is 0.752
 * with a maximum of 0.96, and the alphas above are chosen to stay under it.
 */
export function WarmScrim(props: {
  readonly warm: Warmth; readonly lift: SkyLift; readonly rows: AirRows;
}): JSX.Element | null {
  // The start row is a row of the BACKDROP, and the backdrop is lifted and
  // squeezed to give the sky back its rows - so the row this scrim starts on
  // has to travel with it or a town's warmth begins somewhere over its own
  // sky. Clamped to the back band's floor, past which the air's own table
  // takes over and the stops would stop running downhill.
  const from = Math.min(
    props.lift.shift + props.warm.from * props.lift.squeeze, props.rows.full,
  );
  return (
    <DepthScrim className="hs-town__warm" rgb={WARM_RGB} alpha={props.warm.amount}
      stops={warmStops(from, props.rows)} blend="color" />
  );
}

/**
 * The sun on the near plane.
 *
 * Drawn LAST inside the canvas so it reaches the front band's buildings and
 * the props standing on the promenade as well as the ground under them. That
 * is the difference between lighting a plane and tinting a floor: our
 * foreground was dark in its sprites as much as in its paving, and a scrim
 * under the sprites would have left them dark.
 *
 * SOFT-LIGHT rather than screen, and that was measured rather than chosen. A
 * screen adds the whole of the light to a black and almost none of it to a
 * highlight, which sounds like the right shape for a foreground full of holes
 * and is not: at only 12% a screen of this colour takes a pixel at L=10 to
 * L=36, so the entire sub-36 population of a foreground disappears at once.
 * Shot at the amounts that put each town's foreground on its own reference's,
 * five of the twelve came back with a bottom fifth containing NO pixel below
 * L=36 - Tower and Ironhorde at 0.00% against Tirith's 11.5% and Kruber's
 * 9.1% - which is a flat foreground bought with the mean.
 *
 * Soft-light is a gamma curve rather than an addition: it leaves black black,
 * lifts the midtones, and cannot clip. At the same fitted foreground means the
 * dark fraction over the whole painting comes to 20.3% against the eleven
 * references' 24.0% and the bottom fifth to 23.2% against their 24.0%, where
 * the screen gave 16.1% and 17.2%. It needs two to three times the alpha to
 * move the same mean, which is why these numbers run to 0.90 and the
 * airlight's run to 0.32.
 */
export function NearLight(props: { readonly amount: number; readonly rows: AirRows }): JSX.Element | null {
  return (
    <DepthScrim className="hs-town__near" rgb={NEAR_RGB} alpha={props.amount} stops={nearStops(props.rows)}
      blend="soft-light" />
  );
}

/** One vertical scrim: a stop table, an amount, and how it composites. */
function DepthScrim(props: {
  readonly className: string;
  readonly rgb: string;
  readonly alpha: number;
  readonly stops: readonly (readonly [number, number])[];
  readonly blend?: "screen" | "multiply" | "soft-light" | "color";
}): JSX.Element | null {
  const a = props.alpha;
  if (a <= 0.002) return null;
  const stops = props.stops.map(([y, mul]) =>
    `rgba(${props.rgb},${(a * mul).toFixed(4)}) ${((y / TOWN_CANVAS_H) * 100).toFixed(2)}%`);
  return (
    <div
      className={props.className}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        backgroundImage: `linear-gradient(180deg, ${stops.join(", ")})`,
        ...(props.blend === undefined ? {} : { mixBlendMode: props.blend }),
      }}
    />
  );
}

/**
 * One slot: either the built sprite or an invisible hit region that ghosts in.
 *
 * The box is the frame's OWN size, read from the atlas, not a guess from the
 * slot width. Guessing it was the bug under half the screen's defects: Castle's
 * `dwelling_3` is 185x322 and the guess was 175, so the sprite was drawn 147px
 * below its own ground line - past the street it was supposed to stand on, with
 * its contact shadow and its approach left behind up the hill. Read the frame
 * and every building in the town stands on the same floor.
 */
export function TownBuilding(props: TownBuildingProps): JSX.Element | null {
  const { resolved: r } = props;
  const def = r.built ?? r.next;
  const key = def === null ? null : buildingIcon(def.art);
  const size = useSpriteSize(key, 1);
  if (def === null) return null;
  const built = r.built !== null;

  // Canvas coordinates throughout: the stage is one transformed element, so a
  // building never has to know what the window is doing. The frame ships at
  // TOWN_HD times its drawn size - the band's scale is already in it - so the
  // box is the frame's own size, scaled down by 1/TOWN_HD about its bottom
  // centre, and the ground line lands on the slot's ground line exactly. A
  // frame the atlas has not resolved yet takes the box the contract predicts.
  const k = bandScale(r.slot);
  const w = size?.w ?? Math.round(r.slot.w * k * TOWN_HD);
  const h = size?.h ?? Math.round(expectedHeight(r.slot) * k * TOWN_HD);
  const left = Math.round(r.slot.x - w / 2);
  const top = Math.round(r.slot.y - h);
  const filter = haze(r.slot.band);
  // Always a chain, never `undefined`: the grade is a variable set on the
  // canvas and a building that skipped `--hs-roof` skipped the grade with it,
  // so one unlisted slot name would have stood in yesterday's daylight.
  const roof = [ROOF_TINT[r.slot.name], GROUND_TONE[props.ground], GRADE_VAR]
    .filter((part) => part !== undefined).join(" ");

  return (
    <button
      type="button"
      className={`hs-town__slot${built ? "" : ` hs-town__ghost${props.blocked ? " hs-town__ghost--blocked" : ""}`}`}
      style={{
        left: `${String(left)}px`,
        top: `${String(top)}px`,
        width: `${String(w)}px`,
        height: `${String(h)}px`,
        transform: `scale(${(1 / TOWN_HD).toFixed(6)})`,
        ...(filter === undefined ? {} : { filter }),
        ["--hs-roof" as string]: roof,
      }}
      title={built ? def.name : `Build ${def.name}`}
      aria-label={built ? def.name : `Build ${def.name}`}
      onMouseEnter={() => props.onEnter(def, built)}
      onFocus={() => props.onEnter(def, built)}
      onMouseLeave={props.onLeave}
      onBlur={props.onLeave}
      onClick={props.onOpen}
    >
      {/* THE CAST. The SVG layer under this one throws a tapered
          parallelogram per building, which is the projection of a BOX; a
          blind critic reading the Castle keep at 2x said what that leaves:
          "its wall base meets the pale plateau on a soft airbrushed dark
          ellipse rather than a cast shadow shaped like the walls". A shadow
          is the caster's own silhouette laid on the floor, so this is the
          caster's own frame - the same atlas cell - flattened about its foot
          and sheared along the sun vector, painted flat black and blurred a
          little. It is only drawn for a BUILT structure: a ghosted lot casts
          nothing. */}
      {built ? (
        <ScaledSprite atlasKey={key} scale={1} className="hs-town__silcast" title={def.name} />
      ) : null}
      <ScaledSprite atlasKey={key} scale={1} className="hs-town__art" title={def.name} />
    </button>
  );
}

/**
 * A tree, a hedge or a bench: scenery, drawn in the same order as a building.
 *
 * The magnification is split into a whole part carried by the SPRITE and a
 * remainder carried by the transform, and that split is the whole of the
 * "blurry blobs" defect. A `filter` forces the browser to rasterise an element
 * at its own box size and an ancestor `transform: scale()` then resamples that
 * raster bilinearly - so a foreground occluder at scale 3.4 under a stage at
 * 3/2 was a 64px sprite smeared over 5 device pixels per texel while every
 * building beside it ran at 3/2. Asking `ScaledSprite` for a whole 3x frame
 * instead leaves the transform at 1.13, and `image-rendering: pixelated` on
 * the background does the magnifying with hard edges.
 */
export function TownProp(props: { readonly prop: Prop }): JSX.Element | null {
  const p = props.prop;
  const mag = Math.max(1, Math.round(p.scale));
  const size = useSpriteSize(p.art, mag);
  if (size === null) return null;
  const rest = p.scale / mag;
  const cls = ["hs-town__prop"];
  if (p.fg) cls.push("hs-town__prop--fg");
  if (p.lit) cls.push("hs-town__prop--lit");
  return (
    <span
      className={cls.join(" ")}
      aria-hidden="true"
      style={{
        // The box is the UNSCALED box: `transform-origin: 50% 100%` pins its
        // bottom centre, so that point is the prop's ground line whatever the
        // remainder does to the rest of it.
        left: `${String(Math.round(p.x - size.w / 2))}px`,
        top: `${String(Math.round(p.y - size.h))}px`,
        width: `${String(size.w)}px`,
        height: `${String(size.h)}px`,
        transformOrigin: "50% 100%",
        transform: `scale(${rest.toFixed(4)})${p.flip ? " scaleX(-1)" : ""}`,
        ...(haze(p.band) === undefined ? {} : { filter: haze(p.band) }),
      }}
    >
      <ScaledSprite atlasKey={p.art} scale={mag} className="hs-town__art" />
    </span>
  );
}

